import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  getCategoriasAdmin,
  crearCategoria,
  actualizarCategoria,
} from '../../services/adminService';
import { useInstitucion } from '../../context/InstitucionContext';
import CategoriaSelect from '../../components/admin/CategoriaSelect';
import { idsSubarbol } from '../../utils/categoriasArbol';

const AdminCategoriaForm = () => {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  const history = useHistory();
  const { paleta: colors } = useInstitucion();

  const [categorias, setCategorias] = useState([]);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    padreId: '',
    orden: 0,
    tipoOrden: 'fecha',
  });
  const [slugActual, setSlugActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCategoriasAdmin()
      .then((data) => {
        setCategorias(data);
        if (esEdicion) {
          const cat = data.find((c) => String(c.id) === String(id));
          if (!cat) {
            setError('La categoría no existe.');
            return;
          }
          setForm({
            nombre: cat.nombre,
            descripcion: cat.descripcion || '',
            padreId: cat.padreId != null ? String(cat.padreId) : '',
            orden: cat.orden,
            tipoOrden: cat.tipoOrden || 'fecha',
          });
          setSlugActual(cat.slug);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, esEdicion]);

  // Como padre no se puede elegir la propia categoría ni sus descendientes.
  const opcionesPadre = useMemo(() => {
    if (!esEdicion) return categorias;
    const excluidas = idsSubarbol(categorias, Number(id));
    return categorias.filter((c) => !excluidas.has(c.id));
  }, [categorias, esEdicion, id]);

  // Al editar (mover de padre) no se permite elegir un padre con publicaciones: quedarían
  // invisibles. Al crear sí se permite, porque el sistema ofrece moverlas a la nueva subcategoría.
  const idsPadreDeshabilitados = useMemo(() => {
    if (!esEdicion) return undefined;
    return new Set(opcionesPadre.filter((c) => c.totalPublicaciones > 0).map((c) => c.id));
  }, [esEdicion, opcionesPadre]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.nombre.trim()) {
      setError('Escribe el nombre de la categoría.');
      return;
    }

    const datos = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      padreId: form.padreId ? Number(form.padreId) : null,
      orden: Number(form.orden) || 0,
      tipoOrden: form.tipoOrden,
    };

    // Si el padre elegido ya tiene publicaciones, se mueven a la nueva subcategoría
    // (con confirmación) para que sigan visibles en el sitio.
    if (!esEdicion && datos.padreId) {
      const padre = categorias.find((c) => c.id === datos.padreId);
      if (padre && padre.totalPublicaciones > 0) {
        const confirmado = window.confirm(
          `"${padre.nombre}" tiene ${padre.totalPublicaciones} ${
            padre.totalPublicaciones === 1 ? 'publicación' : 'publicaciones'
          }. Se moverán a la nueva subcategoría "${datos.nombre}" para que sigan visibles en el sitio. ¿Continuar?`
        );
        if (!confirmado) return;
        datos.moverPublicaciones = true;
      }
    }

    setGuardando(true);
    try {
      if (esEdicion) {
        await actualizarCategoria(id, datos);
      } else {
        await crearCategoria(datos);
      }
      history.push('/admin/categorias');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent';

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Cargando...</div>;
  }

  return (
    <div className="max-w-2xl">
      <nav className="mb-6 text-sm">
        <Link to="/admin/categorias" className="font-medium hover:opacity-80" style={{ color: colors.ctaText }}>
          ← Volver a categorías
        </Link>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {esEdicion ? 'Editar categoría' : 'Nueva categoría'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre *</label>
          <input
            type="text"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            required
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
            placeholder="Ej. Boletines Pastoral"
          />
          {esEdicion && slugActual && (
            <p className="text-xs text-gray-400 mt-1">
              Slug: <span className="font-mono">{slugActual}</span> (no cambia al renombrar, para no romper enlaces existentes)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción</label>
          <textarea
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
            rows={3}
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
            placeholder="Breve descripción de la categoría"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoría padre</label>
          <CategoriaSelect
            categorias={opcionesPadre}
            idsDeshabilitados={idsPadreDeshabilitados}
            placeholder="Ninguna (categoría raíz)"
            name="padreId"
            value={form.padreId}
            onChange={handleChange}
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Déjala vacía para que aparezca en la página principal.
            {!esEdicion &&
              ' Si eliges una categoría que ya tiene publicaciones, se te propondrá moverlas a esta nueva subcategoría.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Orden</label>
            <input
              type="number"
              name="orden"
              value={form.orden}
              onChange={handleChange}
              className={inputClass}
              style={{ '--tw-ring-color': colors.primary }}
            />
            <p className="text-xs text-gray-400 mt-1">Posición entre sus categorías hermanas.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Orden de las publicaciones</label>
            <select
              name="tipoOrden"
              value={form.tipoOrden}
              onChange={handleChange}
              className={inputClass}
              style={{ '--tw-ring-color': colors.primary }}
            >
              <option value="fecha">Por fecha (más recientes primero)</option>
              <option value="manual">Manual (campo orden de cada publicación)</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={guardando}
            className="px-5 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            {guardando ? 'Guardando...' : esEdicion ? 'Guardar cambios' : 'Crear categoría'}
          </button>
          <Link to="/admin/categorias" className="px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100">
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminCategoriaForm;
